import { useEffect, useCallback } from 'react';
import { graphql } from 'react-relay';
import { defineMessages, useIntl } from 'react-intl-next';

import { isSmartLinkDrag } from '@atlaskit/editor-smart-link-draggable';
import type { SmartLinkDragData } from '@atlaskit/editor-smart-link-draggable';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractInstruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';

import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop';
import { createSmartLinkEmbedContent } from '@confluence/create-blank-fabric-page/entry-points/dataFunctions.ts';
import { fg } from '@confluence/feature-gating';

import type {
	PageTreeSmartLinkDropMonitorDeleteMutation$data,
	PageTreeSmartLinkDropMonitorDeleteMutation$variables,
} from './__generated__/PageTreeSmartLinkDropMonitorDeleteMutation.graphql.ts';
import { SMART_LINK_DND_ANALYTICS_SOURCE } from './smartLinkDroppableConstants.ts';

const VIRTUAL_ROOT_IDS: ReadonlySet<string> = new Set(['VIRTUAL_ROOT_ID', 'VIRTUAL_LOCAL_ROOT_ID']);

const i18n = defineMessages({
	successTitle: {
		id: 'smart-link-droppable.drop-monitor.success.title',
		description: 'Title for success flag when a smart link is added to the content tree',
		defaultMessage: '"{title}" added to content tree',
	},
	successTitleFallback: {
		id: 'smart-link-droppable.drop-monitor.success.title-fallback',
		description:
			'Title for success flag when a smart link without a title is added to the content tree',
		defaultMessage: 'Link added to content tree',
	},
	undoAction: {
		id: 'smart-link-droppable.drop-monitor.success.undo',
		description: 'Label for undo action on smart link drop success flag',
		defaultMessage: 'Undo',
	},
	undoSuccessTitle: {
		id: 'smart-link-droppable.drop-monitor.undo-success.title',
		description: 'Title for success flag when a smart link drop is undone',
		defaultMessage: 'Smart link removed',
	},
	errorTitle: {
		id: 'smart-link-droppable.drop-monitor.error.title',
		description: 'Title for error flag when adding a smart link to the content tree fails',
		defaultMessage: "We're unable to create right now",
	},
	errorDescription: {
		id: 'smart-link-droppable.drop-monitor.error.description',
		description: 'Description for error flag when adding a smart link to the content tree fails',
		defaultMessage:
			'Refresh or wait a few minutes and try again. If this keeps happening, contact support.',
	},
});

export type SmartLinkInsertParams = {
	contentId: string;
	parentId: string;
	title?: string;
	url: string;
	iconUrl?: string;
	position?: {
		siblingId: string;
		placement: 'before' | 'after';
	};
};

type DropParams = {
	url: string;
	title: string | undefined;
	iconUrl: string | undefined;
	parentId: string;
	instruction: ReturnType<typeof extractInstruction>;
	dropTargetId: string;
};

interface PageTreeSmartLinkDropMonitorProps {
	spaceKey: string;
	onInsert: (params: SmartLinkInsertParams) => void;
	onDelete: (contentId: string) => void;
	flags?: FlagsStateContainer;
}

export function PageTreeSmartLinkDropMonitor({
	spaceKey,
	flags,
	onInsert,
	onDelete,
}: PageTreeSmartLinkDropMonitorProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const [commitMutation] = useMutationInterop<
		PageTreeSmartLinkDropMonitorDeleteMutation$data,
		PageTreeSmartLinkDropMonitorDeleteMutation$variables
	>(graphql`
		mutation PageTreeSmartLinkDropMonitorDeleteMutation(
			$contentId: ID!
			$action: ContentDeleteActionType!
		) {
			deleteContent(contentId: $contentId, action: $action) {
				contentId
			}
		}
	`);

	const deleteSmartLinkEmbed = useCallback(
		(contentId: string) => {
			return commitMutation({
				variables: {
					contentId,
					action: 'MOVE_TO_TRASH',
				},
			});
		},
		[commitMutation],
	);

	const handleUndoSmartLinkEmbed = useCallback(
		(contentId: string) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'smartLinkDragDropUndo',
					source: SMART_LINK_DND_ANALYTICS_SOURCE,
					attributes: { dropTarget: 'contentTree' },
				},
			}).fire();

			onDelete(contentId);
			void deleteSmartLinkEmbed(contentId)
				.then(() => {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'deleted',
							actionSubject: 'smartLink',
							source: SMART_LINK_DND_ANALYTICS_SOURCE,
							attributes: { dropTarget: 'contentTree' },
						},
					}).fire();

					if (flags) {
						void flags.showSuccessFlag({
							title: formatMessage(i18n.undoSuccessTitle),
							isAutoDismiss: true,
						});
					}
				})
				.catch(() => {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'deleteFailed',
							actionSubject: 'smartLink',
							source: SMART_LINK_DND_ANALYTICS_SOURCE,
							attributes: { dropTarget: 'contentTree' },
						},
					}).fire();

					if (flags) {
						void flags.showErrorFlag({
							title: formatMessage(i18n.errorTitle),
							description: formatMessage(i18n.errorDescription),
							isAutoDismiss: true,
						});
					}
				});
		},
		[createAnalyticsEvent, deleteSmartLinkEmbed, flags, formatMessage, onDelete],
	);

	const handleSmartLinkEmbedDrop = useCallback(
		({ url, title, iconUrl, parentId, instruction, dropTargetId }: DropParams) => {
			createSmartLinkEmbedContent({
				createAnalyticsEvent,
				spaceKey,
				parentId: VIRTUAL_ROOT_IDS.has(parentId) ? undefined : parentId,
				embedUrl: url,
			})
				.then(async (result) => {
					const contentId = result.contentId;

					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'created',
							actionSubject: 'smartLink',
							source: SMART_LINK_DND_ANALYTICS_SOURCE,
							attributes: { dropTarget: 'contentTree' },
						},
					}).fire();

					if (contentId) {
						const position =
							instruction &&
							(instruction.type === 'reorder-above' || instruction.type === 'reorder-below')
								? {
										siblingId: dropTargetId,
										placement:
											instruction.type === 'reorder-above'
												? ('before' as const)
												: ('after' as const),
									}
								: undefined;

						onInsert({
							contentId,
							parentId,
							title,
							url,
							iconUrl,
							position,
						});
					}

					if (flags) {
						void flags.showSuccessFlag({
							id: 'smartLinkDropFlag',
							title: title
								? formatMessage(i18n.successTitle, { title })
								: formatMessage(i18n.successTitleFallback),
							isAutoDismiss: true,
							actions: contentId
								? [
										{
											content: formatMessage(i18n.undoAction),
											onClick: () => {
												handleUndoSmartLinkEmbed(contentId);
												void flags.hideFlag('smartLinkDropFlag');
											},
										},
									]
								: undefined,
						});
					}
				})
				.catch(() => {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'createFailed',
							actionSubject: 'smartLink',
							source: SMART_LINK_DND_ANALYTICS_SOURCE,
							attributes: { dropTarget: 'contentTree' },
						},
					}).fire();

					if (flags) {
						void flags.showErrorFlag({
							title: formatMessage(i18n.errorTitle),
							description: formatMessage(i18n.errorDescription),
							isAutoDismiss: true,
						});
					}
				});
		},
		[createAnalyticsEvent, flags, formatMessage, handleUndoSmartLinkEmbed, onInsert, spaceKey],
	);

	useEffect(() => {
		if (!fg('cc_drag_and_drop_smart_link_from_content_to_tree')) {
			return;
		}

		return monitorForElements({
			canMonitor: ({ source }) =>
				isSmartLinkDrag(source.data.type) || (!!source.data.url && !!source.data.appearance),
			onDragStart: () => {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'dragged',
						actionSubject: 'smartLink',
						source: SMART_LINK_DND_ANALYTICS_SOURCE,
					},
				}).fire();
			},
			onDrop: ({ source, location }) => {
				const { url, title, iconUrl } = source.data as unknown as SmartLinkDragData;
				const dropTarget = location?.current?.dropTargets[0];

				if (!url || !dropTarget || !dropTarget.data.id) {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'drop cancelled',
							actionSubject: 'smartLink',
							source: SMART_LINK_DND_ANALYTICS_SOURCE,
						},
					}).fire();
					return;
				}

				const instruction = extractInstruction(dropTarget.data);
				const dropTargetId = dropTarget.data.id as string;

				let parentId: string;
				if (
					instruction &&
					(instruction.type === 'reorder-above' || instruction.type === 'reorder-below')
				) {
					parentId = dropTarget.data.parentId as string;
				} else {
					parentId = dropTargetId;
				}

				handleSmartLinkEmbedDrop({ url, title, iconUrl, parentId, instruction, dropTargetId });
			},
		});
	}, [createAnalyticsEvent, handleSmartLinkEmbedDrop]);

	return null;
}
